import axios from 'axios';
import { API_URL } from '../../config/api';
import SessionService from '../../services/SessionService';
import type { RegisterConfirmItem } from './MendsVisionService';

/**
 * F4 — IA da Empresa: comandos em massa (prévia e progresso do lote) e preferências de envio ditas em frase.
 * Mesmo envelope { data } do resto da API da Mends AI.
 */

const base = `${API_URL}/mends-vision`;

function authHeaders() {
  return { Accept: 'application/json', Authorization: `Bearer ${SessionService.getToken()}` };
}

function camel(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(camel);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k.length ? k[0].toLowerCase() + k.slice(1) : k] = camel(v);
    }
    return out;
  }
  return value;
}

async function unwrap<T>(promise: Promise<{ data: { data?: T; Data?: T } }>): Promise<T> {
  const res = await promise;
  return camel(res.data.data ?? res.data.Data) as T;
}

export interface RegisterBulkLead {
  contactId: number;
  name: string;
  phoneTail?: string | null;
  column?: string | null;
  cardId?: number | null;
  daysWithoutReply?: number | null;
  topics?: string[];
}

export interface RegisterBulkExclusion {
  contactId: number;
  name: string;
  reason: string;
  label: string;
}

export interface RegisterBulkResult {
  contactId: number;
  name: string;
  /** done | queued | skipped | failed | undone */
  outcome: string;
  detail?: string | null;
  approvalId?: number | null;
}

export interface RegisterBulkPreview {
  command: string;
  action: string;
  criteria: string[];
  count: number;
  overLimit: number;
  maxBatch: number;
  leads: RegisterBulkLead[];
  excluded: RegisterBulkExclusion[];
  selector?: string | null;
  selectedAtUtc?: string;
  approval?: string | null;
  /** idle (prévia) | running | done | undone */
  status: string;
  done: number;
  failed: number;
  skipped: number;
  results: RegisterBulkResult[];
}

export interface PreferenceDraft {
  phrase: string;
  understood: boolean;
  rules: unknown;
  lines: string[];
  assumptions: string[];
  notUnderstoodReason?: string | null;
}

export interface MendsAiPreference {
  id: number;
  sectorId: number;
  phrase: string;
  rules: unknown;
  understood: string[];
  enabled: boolean;
  createdBy?: number | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function fetchRegisterAction(sectorId: number, requestId: string, actionId: string): Promise<RegisterConfirmItem> {
  return unwrap(axios.get(`${base}/register-conversation/action`, {
    params: { sectorId, requestId, actionId },
    headers: authHeaders(),
  }));
}

export async function listPreferences(sectorId: number): Promise<MendsAiPreference[]> {
  const data = await unwrap<MendsAiPreference[]>(axios.get(`${base}/preferences`, { params: { sectorId }, headers: authHeaders() }));
  return data || [];
}

export async function interpretPreference(sectorId: number, phrase: string): Promise<PreferenceDraft> {
  return unwrap(axios.post(`${base}/preferences/interpret`, { sectorId, phrase }, { headers: authHeaders() }));
}

export async function savePreference(payload: {
  sectorId: number; id?: number | null; phrase: string; rules: unknown; enabled: boolean;
}): Promise<MendsAiPreference> {
  return unwrap(axios.post(`${base}/preferences`, { ...payload, source: 'settings' }, { headers: authHeaders() }));
}

export async function deletePreference(sectorId: number, id: number): Promise<void> {
  await axios.delete(`${base}/preferences/${id}`, { params: { sectorId }, headers: authHeaders() });
}
